import { Dispatch, SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  retrievePropertiesFromApi,
  retrievePropertiesSuitesFromApi,
  retrievePropertySuitesFromApiByRole,
  retrieveServiceProviderPropertiesFromApi,
} from 'src/pages/SharedPageCustomHooks/UsePropertySuiteSelection/helpers';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import {
  UnitDetailsByProperty,
  UnitDetailsListResponse,
} from 'src/pages/SharedPageCustomHooks/UsePropertySuiteSelection/types';
import { ISuite } from 'src/components/SharedTypes';

interface UsePropertySuiteSelectionParams {
  contactFormData?: Partial<IContactFormData>;
  initialValue?: any;
}

export interface UsePropertySuiteSelectionData {
  errors: Record<string, any>;
  predictions: any[];
  suitesOptions: any[];
  suitesList: ISuite[];
  propertiesList: any[];
  availableProperties: any[];
  availableSuites: UnitDetailsListResponse;
}

export interface UsePropertySuiteSelectionLoading {
  homeownerProperties: boolean;
  propertiesSuites: boolean;
  propertySuite: boolean;
  serviceProviderProperties: boolean;
}

export interface UsePropertySuiteSelectionReturn {
  data: UsePropertySuiteSelectionData;
  loading: UsePropertySuiteSelectionLoading;
  setData: Dispatch<SetStateAction<UsePropertySuiteSelectionData>>;

  fetchHomeownerProperties: () => Promise<void>;
  fetchPropertiesSuites: (params: any) => Promise<void>;
  fetchPropertySuites: (propertyId: any) => Promise<void>;
  fetchServiceProviderManagerProperties: () => Promise<void>;

  updateDisplayedSuitesInUIUponRemovingProperty: (
    selectedPropertyToRemove: any
  ) => void;
  updateDisplayedSuitesInUIUponAddingProperty: (
    selectedPropertyToAdd: any
  ) => Promise<void>;
}

const usePropertySuiteSelection = ({
  initialValue,
  contactFormData,
}: UsePropertySuiteSelectionParams): UsePropertySuiteSelectionReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [data, setData] = useState<UsePropertySuiteSelectionData>({
    errors: {},
    predictions: [],
    suitesOptions: [],
    suitesList: [],
    propertiesList: [],
    availableProperties: [],
    availableSuites: [],
    ...initialValue,
  });

  const [loading, setLoading] = useState({
    homeownerProperties: false,
    propertiesSuites: false,
    propertySuite: false,
    serviceProviderProperties: false,
  });

  const fetchHomeownerProperties = async (): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, homeownerProperties: true }));

    try {
      const propertyData: any = await retrievePropertiesFromApi(user);
      setData((prevState: any) => ({
        ...prevState,
        availableProperties: propertyData,
        propertiesList: contactFormData.id
          ? prevState.propertiesList || []
          : [],
      }));
    } catch (error) {
      console.error('Error in fetchHomeownerProperties:', error);
    } finally {
      setLoading((prev: any) => ({ ...prev, homeownerProperties: false }));
    }
  };

  const fetchPropertiesSuites = async (params: any): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, propertiesSuites: true }));

    try {
      const suitesData: any = await retrievePropertiesSuitesFromApi(params);

      setData((prevState: UsePropertySuiteSelectionData) => ({
        ...prevState,
        availableSuites: suitesData,
        suitesList: contactFormData.id ? prevState.suitesList || [] : [],
      }));
    } catch (error) {
      console.error('Error in fetchPropertiesSuites:', error);
    } finally {
      setLoading((prev: any) => ({ ...prev, propertiesSuites: false }));
    }
  };

  const fetchPropertySuites = async (propertyId: any): Promise<void> => {
    setLoading((prev) => ({ ...prev, propertyS: true }));

    try {
      const suitesData: any = await retrievePropertySuitesFromApiByRole(
        propertyId,
        user.id,
        user.roles[0].role.name
      );
      setData((prevState: UsePropertySuiteSelectionData) => ({
        ...prevState,
        availableSuites: suitesData,
        suitesList: suitesData,
      }));
    } catch (error) {
      console.error('Error in fetchPropertySuites:', error);
    } finally {
      setLoading((prev: any) => ({ ...prev, propertyS: false }));
    }
  };

  const fetchServiceProviderManagerProperties = async (): Promise<void> => {
    setLoading((prev: any) => ({ ...prev, serviceProviderProperties: true }));

    try {
      const propertiesData: any =
        await retrieveServiceProviderPropertiesFromApi(contactFormData.id);

      const propertiesByUser: any = await retrievePropertiesFromApi(user);

      setData((prevState: UsePropertySuiteSelectionData) => ({
        ...prevState,
        suitesList: [],
        propertiesList: propertiesData,
        availableProperties: propertiesByUser,
      }));
    } catch (error) {
      console.error('Error in fetchServiceProviderProperties:', error);
    } finally {
      setLoading((prev: any) => ({
        ...prev,
        serviceProviderProperties: false,
      }));
    }
  };

  // Orchestrate removing suites from the UI state for the dropdown menu
  const updateDisplayedSuitesInUIUponRemovingProperty = (
    selectedPropertyToRemove: any
  ): void => {
    const propertyIdToRemove =
      selectedPropertyToRemove.Id || selectedPropertyToRemove.id;

    const currentState: UsePropertySuiteSelectionData = data;

    const filteredAvailableSuites: UnitDetailsByProperty[] = currentState.availableSuites.filter(
      (suite: any) => suite.propertyId !== propertyIdToRemove
    );

    const filteredSuitesList: ISuite[] = currentState.suitesList.filter(
      (suite: any) => suite.propertyId !== propertyIdToRemove
    );

    const updatedState = {
      ...currentState,
      availableSuites: filteredAvailableSuites,
      suitesList: filteredSuitesList,
    };

    setData(updatedState);
  };

  const updateDisplayedSuitesInUIUponAddingProperty = async (
    selectedPropertyToAdd: any
  ) => {
    const propertyIdToAdd =
      selectedPropertyToAdd.Id || selectedPropertyToAdd.id;

    try {
      setLoading((prev: any) => ({ ...prev, propertySuite: true }));

      const newSuitesFromApi: any = await retrievePropertySuitesFromApiByRole(
        propertyIdToAdd,
        user.id,
        user.roles[0].role.name
      );

      const currentState: UsePropertySuiteSelectionData = data;

      const existingSuiteIds = new Set(
        currentState.availableSuites.map((suite: any) => suite.id)
      );

      const uniqueNewSuites = newSuitesFromApi.filter(
        (newSuite: any) => !existingSuiteIds.has(newSuite.id)
      );

      const updatedAvailableSuites = [
        ...currentState.availableSuites,
        ...uniqueNewSuites,
      ];
      const updatedSuitesList = [
        ...currentState.suitesList,
        ...uniqueNewSuites,
      ];

      const updatedState = {
        ...currentState,
        availableSuites: updatedAvailableSuites,
        suitesList: updatedSuitesList,
      };

      setData(updatedState);
    } catch (error) {
      console.error('Error fetching suites for new property:', error);
      const currentState: UsePropertySuiteSelectionData = data;
      const errorMessage = 'Failed to load suites for selected property';
      const updatedErrors = {
        ...currentState.errors,
        property: errorMessage,
      };
      const errorState = {
        ...currentState,
        errors: updatedErrors,
      };
      setData(errorState);
      throw error;
    }
  };

  return {
    data,
    setData,
    loading,
    fetchHomeownerProperties,
    fetchPropertiesSuites,
    fetchPropertySuites,
    fetchServiceProviderManagerProperties,
    updateDisplayedSuitesInUIUponRemovingProperty,
    updateDisplayedSuitesInUIUponAddingProperty,
  };
};

export default usePropertySuiteSelection;
